import { ChevronDown, FolderPlus, Folder } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";

interface FolderSelectorProps {
  folders: string[];
  selectedFolder: string;
  onFolderSelect: (folder: string) => void;
  onCreateFolder: (folderName: string) => void;
  loading?: boolean;
  label?: string;
  placeholder?: string;
}

const FolderSelector: React.FC<FolderSelectorProps> = ({
  folders,
  selectedFolder,
  onFolderSelect,
  onCreateFolder,
  loading = false,
  label = "Folder",
  placeholder = "Select a folder",
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsCreating(false);
        setNewFolderName("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus input when creating
  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreating]);

  const handleFolderClick = (folder: string) => {
    onFolderSelect(folder);
    setIsOpen(false);
  };

  const handleCreateClick = () => {
    setIsCreating(true);
  };

  const handleCreateSubmit = () => {
    const trimmed = newFolderName.trim();
    if (trimmed) {
      onCreateFolder(trimmed);
      onFolderSelect(trimmed);
      setNewFolderName("");
      setIsCreating(false);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCreateSubmit();
    } else if (e.key === "Escape") {
      setIsCreating(false);
      setNewFolderName("");
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label
          className={`block text-sm font-medium mb-1 ${
            isDarkMode ? "text-rp-grey/70" : "text-gray-700"
          }`}
        >
          {label}
        </label>
      )}

      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
          isDarkMode
            ? "bg-rp-surface border-rp-separator text-rp-grey hover:bg-rp-surface-raised"
            : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
        } ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="flex items-center space-x-2">
          <Folder className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`} />
          <span className={selectedFolder ? "" : isDarkMode ? "text-rp-grey/50" : "text-gray-500"}>
            {selectedFolder || placeholder}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""} ${
            isDarkMode ? "text-rp-grey/50" : "text-gray-500"
          }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={`absolute z-50 mt-1 w-full rounded-lg shadow-lg border ${
            isDarkMode ? "bg-rp-surface border-rp-separator" : "bg-white border-gray-200"
          }`}
        >
          <div className="max-h-60 overflow-auto py-1">
            {/* Existing folders */}
            {folders.length > 0 ? (
              folders.map((folder) => (
                <button
                  key={folder}
                  onClick={() => handleFolderClick(folder)}
                  className={`w-full flex items-center space-x-2 px-3 py-2 text-sm text-left transition-colors ${
                    folder === selectedFolder
                      ? isDarkMode
                        ? "bg-teal-900/30 text-teal-400"
                        : "bg-teal-50 text-teal-700"
                      : isDarkMode
                        ? "text-rp-grey hover:bg-rp-surface-raised"
                        : "text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <Folder className="w-4 h-4" />
                  <span>{folder}</span>
                </button>
              ))
            ) : (
              <div
                className={`px-3 py-2 text-sm ${
                  isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                }`}
              >
                No folders yet
              </div>
            )}
          </div>

          {/* Divider */}
          <div className={`border-t ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`} />

          {/* Create new folder */}
          {isCreating ? (
            <div className="p-2">
              <div className="flex items-center space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Folder name"
                  className={`flex-1 px-3 py-1.5 text-sm rounded border ${
                    isDarkMode
                      ? "bg-rp-surface border-rp-separator text-rp-grey placeholder-rp-grey/40"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  } focus:outline-none focus:ring-2 focus:ring-teal-500`}
                />
                <button
                  onClick={handleCreateSubmit}
                  disabled={!newFolderName.trim()}
                  className={`px-3 py-1.5 text-sm rounded transition-colors ${
                    newFolderName.trim()
                      ? "bg-teal-600 text-white hover:bg-teal-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Create
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleCreateClick}
              className={`w-full flex items-center space-x-2 px-3 py-2 text-sm transition-colors ${
                isDarkMode
                  ? "text-teal-400 hover:bg-rp-surface-raised"
                  : "text-teal-600 hover:bg-gray-50"
              }`}
            >
              <FolderPlus className="w-4 h-4" />
              <span>Create new folder</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FolderSelector;

